import {err, ok, ValidationError} from 'framework';
import type {Result, Validator} from 'framework';
import * as z from 'zod';
import type {WorkerRequest, WorkerResponse} from './worker-protocol';

const idSchema = z.string().min(1);

const errorCodeSchema = z.enum([
  'unreadable',
  'unsupported-format',
  'input-too-large',
  'too-many-pixels',
  'decode-failed',
  'encode-failed',
  'output-too-large',
  'aborted',
]);

const contentKindSchema = z.enum(['photo', 'graphic']);

const dimensionSchema = z.number().int().positive();

const cropSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number().positive(),
  height: z.number().positive(),
});

const optionsSchema = z.object({
  constraints: z.object({
    acceptedMimeTypes: z.array(z.string()),
    maxBytes: z.number().int().positive(),
  }),
  maxLongEdgePx: z.number().int().positive(),
  contentHint: z.enum(['auto', 'photo', 'graphic']),
  crop: cropSchema.nullable(),
});

const requestSchema: z.ZodType<WorkerRequest> = z.discriminatedUnion('kind', [
  z.object({id: idSchema, kind: z.literal('preprocess'), file: z.file(), options: optionsSchema}),
  z.object({id: idSchema, kind: z.literal('abort')}),
]);

const preparedSchema = z.discriminatedUnion('disposition', [
  z.object({
    disposition: z.literal('processed'),
    file: z.file(),
    format: z.enum(['image/jpeg', 'image/png']),
    kind: contentKindSchema,
    width: dimensionSchema,
    height: dimensionSchema,
    sourceWidth: dimensionSchema,
    sourceHeight: dimensionSchema,
  }),
  z.object({
    disposition: z.literal('passed-through'),
    file: z.file(),
    format: z.enum(['image/jpeg', 'image/png', 'image/webp']),
    kind: contentKindSchema,
    width: dimensionSchema,
    height: dimensionSchema,
  }),
  z.object({disposition: z.literal('fallback'), file: z.file(), reason: errorCodeSchema}),
]);

const responseSchema: z.ZodType<WorkerResponse> = z.discriminatedUnion('kind', [
  z.object({id: idSchema, kind: z.literal('ok'), prepared: preparedSchema}),
  z.object({id: idSchema, kind: z.literal('error'), code: errorCodeSchema}),
]);

/** Messages arriving in the worker are untrusted input like any other boundary. */
export class WorkerRequestValidator implements Validator<WorkerRequest> {
  readonly standardSchema = requestSchema;

  validate(value: unknown): Result<WorkerRequest, ValidationError> {
    const parsed = this.standardSchema.safeParse(value);
    return parsed.success
      ? ok(parsed.data)
      : err(new ValidationError(parsed.error.issues[0]?.message ?? 'Invalid worker request.'));
  }
}

/** The main thread trusts nothing that comes back through `postMessage` either. */
export class WorkerResponseValidator implements Validator<WorkerResponse> {
  readonly standardSchema = responseSchema;

  validate(value: unknown): Result<WorkerResponse, ValidationError> {
    const parsed = this.standardSchema.safeParse(value);
    return parsed.success
      ? ok(parsed.data)
      : err(new ValidationError(parsed.error.issues[0]?.message ?? 'Invalid worker response.'));
  }
}
